import React, { useState, useEffect } from 'react';
import { View, ScrollView, TouchableOpacity } from 'react-native';
import { Text } from '@/components';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Waves, TreePine, TrafficCone, Mountain, Sun, Route, Cloud, CloudSun } from 'lucide-react-native';
import styles from './review.styles';
import theme from '@/constants/colors';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';
import { loadEndWalkDraft, saveEndWalkDraft } from '@/utils/endWalkDraft';

const DESCRIPTIONS = ['beach', 'forest', 'road', 'cliff'];
const DESCRIPTION_ICONS = [Waves, TreePine, TrafficCone, Mountain];

type ChipLevel = 'low' | 'mid' | 'high' | null;
type PathWidth = 'wide' | 'narrow' | null;
type YesNo = 'yes' | 'no' | null;

// Map chip selections to the 1-100 values the API expects
const LEVEL_MAP: Record<string, number> = { low: 25, mid: 50, high: 75 };
const PATH_MAP: Record<string, number> = { wide: 25, narrow: 75 };

// 1-5 traffic scale → 0-100 range
const TRAFFIC_MAP: Record<number, number> = { 1: 10, 2: 30, 3: 50, 4: 70, 5: 100 };
const TRAFFIC_LABELS = ['quiet', 'light', 'some', 'busy', 'lots'];

export default function EndWalkReview() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const draftId = typeof params.draftId === 'string' ? params.draftId : undefined;

  const [draft, setDraft] = useState<any>(undefined);

  useEffect(() => {
    if (!draftId) {
      try {
        if (typeof params.draft === 'string') {
          setDraft(JSON.parse(decodeURIComponent(params.draft)));
        }
      } catch (err) {
        console.warn('Failed to parse draft param in end-walk review', err);
      }
      return;
    }
    let mounted = true;
    loadEndWalkDraft(draftId).then((loaded) => {
      if (mounted) setDraft(loaded ?? undefined);
    });
    return () => { mounted = false; };
  }, [draftId, params.draft]);

  // Star rating (may come from summary page)
  const [rating, setRating] = useState<number>(draft?.rating || 0);

  // Environment tags
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  // Traffic 1-5 scale
  const [dogTraffic, setDogTraffic] = useState<number | null>(null);
  const [footTraffic, setFootTraffic] = useState<number | null>(null);
  const [exposure, setExposure] = useState<ChipLevel>(null);
  const [paths, setPaths] = useState<PathWidth>(null);

  // Yes/No chips (replacing toggles)
  const [offLeash, setOffLeash] = useState<YesNo>(null);
  const [wildlife, setWildlife] = useState<YesNo>(null);

  useEffect(() => {
    if (draft?.rating) setRating(draft.rating);
  }, [draft?.rating]);

  const toggleDescription = (idx: number) => {
    Haptics.selectionAsync();
    setSelected(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const selectDogTraffic = (level: number) => {
    Haptics.selectionAsync();
    setDogTraffic(prev => prev === level ? null : level);
  };

  const selectFootTraffic = (level: number) => {
    Haptics.selectionAsync();
    setFootTraffic(prev => prev === level ? null : level);
  };

  const selectExposure = (level: ChipLevel) => {
    Haptics.selectionAsync();
    setExposure(prev => prev === level ? null : level);
  };

  const selectPaths = (width: PathWidth) => {
    Haptics.selectionAsync();
    setPaths(prev => prev === width ? null : width);
  };

  const selectOffLeash = (val: YesNo) => {
    Haptics.selectionAsync();
    setOffLeash(prev => prev === val ? null : val);
  };

  const selectWildlife = (val: YesNo) => {
    Haptics.selectionAsync();
    setWildlife(prev => prev === val ? null : val);
  };

  const goBack = async () => {
    const nextDraftId = await saveEndWalkDraft(draft || {}, draftId);
    router.push(`/end-walk/summary?draftId=${nextDraftId}`);
  };

  const goNext = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const picked = DESCRIPTIONS.filter((_, i) => selected[i]);
    const enhanced = {
      ...draft,
      rating,
      tags: picked,
      dogTraffic: dogTraffic ? TRAFFIC_MAP[dogTraffic] : 50,
      footTraffic: footTraffic ? TRAFFIC_MAP[footTraffic] : 50,
      paths: paths ? PATH_MAP[paths] : 50,
      exposure: exposure ? LEVEL_MAP[exposure] : 50,
      offLeash: offLeash === 'yes',
      wildlife: wildlife === 'yes',
    };
    const nextDraftId = await saveEndWalkDraft(enhanced, draftId);
    router.push(`/end-walk/info?draftId=${nextDraftId}`);
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={[styles.container, { paddingTop: insets.top + 20 }]}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>

          <View style={styles.bigHeader}>
            <Text style={styles.bottomTitle}>{t('trailEdit.describeTheTrail')}</Text>
            <Text style={styles.bottomSubtitle}>{t('trailEdit.tapWhatApplies')}</Text>
          </View>

          <View style={{ paddingHorizontal: 8 }}>

            {/* Environment tags */}
            <Text style={styles.sectionLabel}>{t('trailEdit.environment')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
              {DESCRIPTIONS.map((d, i) => {
                const Icon = DESCRIPTION_ICONS[i];
                return (
                  <TouchableOpacity key={d} onPress={() => toggleDescription(i)} style={[styles.chip, selected[i] && styles.chipSelected]}>
                    <Icon size={20} color={selected[i] ? theme.backgroundPrimary : theme.textMuted} />
                    <Text style={[styles.chipText, selected[i] && styles.chipTextSelected]}>{t(`explore.${d}`)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={{ height: 28 }} />

            {/* Dogs around? — 1-5 scale */}
            <Text style={styles.sectionLabel}>{t('trailEdit.dogsAround')}</Text>
            <View style={styles.trafficRow}>
              {[1, 2, 3, 4, 5].map((level) => (
                <TouchableOpacity
                  key={level}
                  onPress={() => selectDogTraffic(level)}
                  style={[styles.trafficButton, dogTraffic === level && styles.trafficButtonSelected]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.trafficButtonNumber, dogTraffic === level && styles.trafficButtonNumberSelected]}>{level}</Text>
                  <Text style={[styles.trafficButtonLabel, dogTraffic === level && styles.trafficButtonLabelSelected]}>{t(`filters.${TRAFFIC_LABELS[level - 1]}`)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={{ height: 20 }} />

            {/* People around? — 1-5 scale */}
            <Text style={styles.sectionLabel}>{t('trailEdit.peopleAround')}</Text>
            <View style={styles.trafficRow}>
              {[1, 2, 3, 4, 5].map((level) => (
                <TouchableOpacity
                  key={level}
                  onPress={() => selectFootTraffic(level)}
                  style={[styles.trafficButton, footTraffic === level && styles.trafficButtonSelected]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.trafficButtonNumber, footTraffic === level && styles.trafficButtonNumberSelected]}>{level}</Text>
                  <Text style={[styles.trafficButtonLabel, footTraffic === level && styles.trafficButtonLabelSelected]}>{t(`filters.${TRAFFIC_LABELS[level - 1]}`)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={{ height: 20 }} />

            {/* Shade? */}
            <Text style={styles.sectionLabel}>{t('trailEdit.shade')}</Text>
            <View style={styles.chipRow}>
              <TouchableOpacity onPress={() => selectExposure('low')} style={[styles.levelChip, exposure === 'low' && styles.levelChipSelected]}>
                <Cloud size={18} color={exposure === 'low' ? theme.backgroundPrimary : theme.textMuted} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => selectExposure('mid')} style={[styles.levelChip, exposure === 'mid' && styles.levelChipSelected]}>
                <CloudSun size={18} color={exposure === 'mid' ? theme.backgroundPrimary : theme.textMuted} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => selectExposure('high')} style={[styles.levelChip, exposure === 'high' && styles.levelChipSelected]}>
                <Sun size={18} color={exposure === 'high' ? theme.backgroundPrimary : theme.textMuted} />
              </TouchableOpacity>
            </View>

            <View style={{ height: 20 }} />

            {/* Paths? */}
            <Text style={styles.sectionLabel}>{t('trailEdit.paths')}</Text>
            <View style={styles.chipRow}>
              <TouchableOpacity onPress={() => selectPaths('wide')} style={[styles.levelChip, paths === 'wide' && styles.levelChipSelected]}>
                <Route size={16} color={paths === 'wide' ? theme.backgroundPrimary : theme.textMuted} />
                <Text numberOfLines={1} style={[styles.levelChipText, paths === 'wide' && styles.levelChipTextSelected]}>{t('trailEdit.wideOpen')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => selectPaths('narrow')} style={[styles.levelChip, paths === 'narrow' && styles.levelChipSelected]}>
                <Text numberOfLines={1} style={[styles.levelChipText, paths === 'narrow' && styles.levelChipTextSelected]}>{t('trailEdit.narrow')}</Text>
              </TouchableOpacity>
            </View>

            <View style={{ height: 24 }} />

            {/* Off-leash */}
            <Text style={styles.sectionLabel}>{t('trailEdit.offLeashFriendly')}</Text>
            <View style={styles.chipRow}>
              <TouchableOpacity onPress={() => selectOffLeash('yes')} style={[styles.levelChip, offLeash === 'yes' && styles.levelChipSelected]}>
                <Text numberOfLines={1} style={[styles.levelChipText, offLeash === 'yes' && styles.levelChipTextSelected]}>{t('common.yes')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => selectOffLeash('no')} style={[styles.levelChip, offLeash === 'no' && styles.levelChipSelected]}>
                <Text numberOfLines={1} style={[styles.levelChipText, offLeash === 'no' && styles.levelChipTextSelected]}>{t('common.no')}</Text>
              </TouchableOpacity>
            </View>

            <View style={{ height: 20 }} />

            {/* Wildlife */}
            <Text style={styles.sectionLabel}>{t('trailEdit.wildlifeLivestock')}</Text>
            <View style={styles.chipRow}>
              <TouchableOpacity onPress={() => selectWildlife('yes')} style={[styles.levelChip, wildlife === 'yes' && styles.levelChipSelected]}>
                <Text numberOfLines={1} style={[styles.levelChipText, wildlife === 'yes' && styles.levelChipTextSelected]}>{t('common.yes')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => selectWildlife('no')} style={[styles.levelChip, wildlife === 'no' && styles.levelChipSelected]}>
                <Text numberOfLines={1} style={[styles.levelChipText, wildlife === 'no' && styles.levelChipTextSelected]}>{t('common.no')}</Text>
              </TouchableOpacity>
            </View>

            <View style={{ height: 24 }} />
          </View>
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: insets.bottom + 20 }]}>
          <TouchableOpacity style={styles.backButton} onPress={goBack}>
            <Text style={styles.backButtonText}>{t('common.back')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.nextButtonLarge} onPress={goNext}>
            <Text style={styles.nextButtonText}>{t('common.next')} →</Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
}
